/**
 * Starting a payment.
 *
 * One call, and then the page is gone: the Worker creates a Checkout Session
 * and answers with a URL on Stripe's own host, and the tab navigates there.
 * It does not open a popup and it does not embed anything.
 *
 * The embedding half of that is not a preference between two things that
 * work, and it is worth setting out, because Stripe publishes the obvious
 * alternative — the Payment Element, or embedded Checkout: a card form inside
 * our own page, which keeps the learner where they are and looks like the
 * upgrade somebody will one day try to make this into. It cannot work here.
 * The app is cross-origin isolated for the emulator's sake (docs/browser.md,
 * decision 8), which means COEP `require-corp`, and a document under
 * `require-corp` will only load a cross-origin frame that opts in by sending
 * `require-corp` itself — a promise that cascades, because everything inside
 * *that* page would then have to opt in too. Stripe's frames do not make that
 * promise and could not without remaking their own page: a checkout pulls in
 * fraud signals, wallet frames and bank redirects from origins it does not
 * own and cannot answer for. So the embedded form does not degrade here, it
 * does not appear — an empty rectangle on the one screen in the product that
 * takes money. And it would look perfectly fine right up until it shipped,
 * because the dev server sets no isolation headers at all (vite.config.ts):
 * the rectangle is blank in production and full on the machine of whoever
 * decided the redirect was old-fashioned.
 *
 * Sign-in reached the same place from the same constraint (lib/account.ts):
 * the provider's page is somewhere the browser goes, never something this
 * document contains. It differs only in going there in a popup, and that is
 * about the emulator rather than about COEP — sign-in is offered from the
 * chat dock, which is mounted on every screen including a level in progress,
 * and a navigation away from one of those is a running Linux thrown on the
 * floor. Buying is pressed from two screens and neither of them is a level:
 * the offer block at the foot of the ledger, and the debrief. App.tsx renders
 * both of those *instead of* `Play` rather than inside it, so a full-page
 * navigation here costs nothing anybody was in the middle of.
 *
 * Nothing here decides whether anybody has paid. The learner comes back to
 * `/?checkout=…`, which is a hint for the copy and nothing more; the
 * entitlement was settled server-side by Stripe's signed webhook
 * (worker/stripe.ts), and the tab finds out by asking (`usePaid`). A
 * `?checkout=paid` in the address bar is a string the learner could have
 * typed themselves, and it buys exactly as much as that.
 */

/** What happened, from the tab's point of view. `redirecting` means the page is on its way out. */
export type CheckoutResult = 'redirecting' | 'anonymous' | 'unavailable' | 'owned' | 'failed';

/**
 * The two lengths of the course that can be bought (worker/stripe.ts,
 * `Plan`). Three months is what every button meant before the month existed,
 * and what one that does not say means now.
 */
export type Plan = 'quarter' | 'month';

/**
 * Three months of the course, or one, in this tab.
 *
 * `owned` now means "a period is running and it is not in its last
 * fortnight": the Worker opens the checkout again for a renewal inside the
 * last fourteen days (worker/stripe.ts), and refuses it earlier.
 */
export async function startCheckout(plan: Plan = 'quarter'): Promise<CheckoutResult> {
  // The three months are still a body-less POST, the request every client
  // before the month sent.
  const created = await createCheckout(plan === 'month' ? 'month' : null);
  if (typeof created !== 'string') return created.result;
  window.location.assign(created);
  return 'redirecting';
}

/** The Worker's answer: a URL on Stripe's host, or why there is none. */
async function createCheckout(product: string | null): Promise<string | { result: CheckoutResult }> {
  let res: Response;
  try {
    res = await fetch('/api/checkout', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      ...(product ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ product }) } : {}),
    });
  } catch {
    return { result: 'unavailable' };
  }
  // A payment needs an account to attach to: there has to be somebody to give
  // the course to afterwards.
  if (res.status === 401) return { result: 'anonymous' };
  // A guest account (worker/account.ts) has nobody to give the course to
  // either: no address to sign back in with. Asked to sign in, the same way.
  if (res.status === 403 && (await res.clone().json().catch(() => null))?.code === 'guest') return { result: 'anonymous' };
  // 404 is the Worker saying the route does not exist, which is what a
  // deployment without the four Stripe values set looks like
  // (worker/stripe.ts). Not an error the learner caused, and not one they can
  // do anything about.
  if (res.status === 404) return { result: 'unavailable' };
  // The Worker refuses a second purchase by an account that already owns the
  // course, and it refuses before it creates the Checkout Session, so nothing
  // reached Stripe either and there is no half-started session at their end
  // for anybody to reconcile later. It is its own answer rather than a failure
  // because the two want opposite sentences: "try again in a moment" is
  // exactly the wrong advice for somebody who needs to be told they do not
  // have to.
  if (res.status === 409) return { result: 'owned' };
  if (!res.ok) return { result: 'failed' };

  const body = (await res.json()) as { url?: unknown };
  if (typeof body.url !== 'string' || !body.url) return { result: 'failed' };
  return body.url;
}

/**
 * What the URL says about a checkout we have just come back from, and the
 * removal of it from the address bar.
 *
 * Read once and then erased, so that a reload does not replay the message and
 * a shared link does not carry somebody else's outcome. `replaceState` rather
 * than a navigation: the app is a single page and the level route lives in
 * the same query string.
 */
export function takeCheckoutHint(): 'paid' | 'cancelled' | null {
  const params = new URLSearchParams(window.location.search);
  const hint = params.get('checkout');
  if (hint !== 'paid' && hint !== 'cancelled') return null;
  params.delete('checkout');
  const query = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  return hint;
}
